import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext, QuestProprietaryDataIcon } from "@app/overviews/network-benchmarking-dashboard/filters";
import { QHPNetwork, QHPNetworkFlyout } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { getNetworkTooltip } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/utility";
import { TooltipWrapper } from "@app/shared";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, Waffles, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { QESButton } from "@app/shared/qes-standards";

interface QHPNetworkFiltersTableProps {
  data: QHPNetwork[];
  parentOrganizationName: string;
}

interface QHPNetworkFiltersTableData {
  contractName: JSX.Element;
  countyCount: JSX.Element;
  rating: JSX.Element;
  rowIsDisabled?: boolean;
  type: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  contractName: {
    alignment: TextAlignment.Left,
    displayText: "",
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "contract-name",
  },
  countyCount: {
    alignment: TextAlignment.Center,
    displayText: "County Count",
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "county-count",
  },
  rating: {
    alignment: TextAlignment.Center,
    displayText: "Rating",
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "rating",
  },
  type: {
    alignment: TextAlignment.Center,
    displayText: "Type",
    noResize: true,
    noSort: true,
    sizeContentToFit: true,
    testId: "plan-type",
  },
};

const QHPNetworkFiltersTable = (props: QHPNetworkFiltersTableProps): JSX.Element => {
  const [showingMore, setShowingMore] = useState<boolean>(false);
  const [tableData, setTableData] = useState<QHPNetworkFiltersTableData[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext)

  useEffect(() => {
    if (!!props.data) {
      /* eslint-disable sort-keys */
      const newTableData: QHPNetworkFiltersTableData[] = (showingMore ? props.data : props.data.slice(0, 3)).map((contract, index) => {
        return {
          [wafflesKeyColumnName]: contract.contractId || index.toString(),
          contractName: (
            <>
              <FlyoutLink
                classNames="qes-button qes-button-secondary h-contract-flyout-link"
                testId={`network-benchmarking-dashboard--network-filters__flyout-link-${contract.contractId}`}
                content={{
                  body: (
                    <QHPNetworkFlyout
                      contract={contract}
                    />
                  ),
                  title: `Network ${getNetworkId(contract)}`,
                }}
              >
                <TooltipWrapper
                  text={getNetworkTooltip(contract)}
                >
                  {contract.networkName}
                </TooltipWrapper>
              </FlyoutLink>
              <QuestProprietaryDataIcon />
              {contract.dentalOnly && (
                <TooltipWrapper
                  containerClassName="mr-1"
                  text="Stand-Alone Dental Plan (SADP) Network"
                >
                  <img className="d-block" src={`${window.customUIBaseUrl}/images/purple-tooth.svg`} alt="" />
                </TooltipWrapper>
              )}
            </>
          ),
          type: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {getPlanTypeShortenedName(planType.planType)}
                  </div>
                )
              )) : (
                "---"
              )}
            </React.Fragment>
          ),
          countyCount: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {printNumberAsString(contract.countyCount, "---")}
                  </div>
                )
              )) : (
                printNumberAsString(contract.countyCount, "---")
              )}
            </React.Fragment>
          ),
          rating: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {printNumberAsString(planType.starRating, "---")}
                  </div>
                )
              )) : (
                "---"
              )}
            </React.Fragment>
          ),
          // disable row if contract is disabled (due to lack of data) OR
          // five contracts have already been selected and the row isn't one of the selected h contracts
          rowIsDisabled: contract.disabled || (
            benchmarkingFiltersContext.selectedNetworks.length === 5 && !benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId).includes(contract.contractId)
          ),
        }
      });
      /* eslint-enable sort-keys */

      setTableData(newTableData);
    }
  }, [props.data, benchmarkingFiltersContext.selectedNetworks, showingMore]);

  const getNetworkId = (contract: QHPNetwork): string => {
    const splitContract: string[] = contract.contractId.split("_");

    if (!!splitContract?.length) {
      return splitContract[1];
    }
  }

  const getPlanTypeShortenedName = (planType: string): string => {
    if (!planType) {
      return "";
    } else if (planType.toUpperCase().includes("MEDICARE-MEDICAID")) {
      return "MMP";
    } else if (planType.toUpperCase().includes("1876 COST")) {
      return "1876";
    } else if (planType.toUpperCase().includes("HMO/HMOPOS")) {
      return "HMO";
    } else if (planType.toUpperCase().includes("LOCAL PPO") || planType.toUpperCase().includes("REGIONAL PPO")) {
      return "PPO";
    } else {
      return planType;
    }
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    // obtain the h contract models based off their contract ids
    const selectedHContracts: QHPNetwork[] = cloneDeep(props.data).filter(
      (x) => selectedKeys.includes(x.contractId.toString())
    );

    // since toggling a waffles checkbox returns all of the currently-selected keys
    // and the selected h contracts array can include h contracts from different parent organizations,
    // filter out all h contracts that correspond to this parent organization from the select h contracts...
    const newSelectedHContracts: QHPNetwork[] = cloneDeep((benchmarkingFiltersContext.selectedNetworks as QHPNetwork[])).filter(
      (x) => !props.data.map(d => d.contractId).includes(x.contractId)
    );

    // ...and then add back the selected h contracts for this parent organization
    benchmarkingFiltersContext.setSelectedNetworks(newSelectedHContracts.concat(selectedHContracts));
  }

  return (
    <React.Fragment>
      <Waffles<QHPNetworkFiltersTableData>
        columns={columnConfig}
        data={tableData}
        disableStickyHeader={true}
        fullWidth={true}
        handleCheckboxChange={handleCheckboxChange}
        keyColumns={["contractName"]}
        selectedKeys={benchmarkingFiltersContext.selectedNetworks.map((x) => x.contractId.toString())} //adjust so it's only for this tile
        showCheckboxes={true}
        testId={`network-benchmarking-dashboard--network-filters__table-${props.parentOrganizationName}`}
        theme={WafflesThemes.Simple}
      />
      {props.data.length > 3 && (
        <QESButton
          qesTestId={`network-benchmarking-dashboard__network-filters--table-show-more-less-${props.parentOrganizationName?.replace(/\W/g, "")}`}
          qesType="secondary"
          onClick={() => setShowingMore(!showingMore)}
          className="px-0 float-right"
        >
          Show {showingMore ? "Less" : "More"}
        </QESButton>
      )}
    </React.Fragment>

  )
}

export default QHPNetworkFiltersTable;